import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { FolderTree, Plus, Pencil, Trash2, X } from 'lucide-react';
import './Expenses.css';

const emptyCategory = {
  name: '',
  parentId: '',
  notes: ''
};

// Build a lookup of id -> category for name resolution
export const buildCategoryLookup = (categories = []) => {
  const map = {};
  categories.forEach(c => { map[c.id] = c; });
  return map;
};

// Returns the top-most ancestor for P&L rollup. If a category has no parent, it is its own root.
export const getRootCategory = (categoryId, lookup) => {
  let current = lookup[categoryId];
  const seen = new Set();
  while (current && current.parentId && lookup[current.parentId] && !seen.has(current.id)) {
    seen.add(current.id);
    current = lookup[current.parentId];
  }
  return current || null;
};

function ExpenseCategories() {
  const { state, addExpenseCategory, updateExpenseCategory, deleteExpenseCategory } = useApp();
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(emptyCategory);

  const categories = useMemo(() => state.expenseCategories || [], [state.expenseCategories]);
  const lookup = useMemo(() => buildCategoryLookup(categories), [categories]);

  // Tree structure: parents -> children
  const { parents, childrenByParent } = useMemo(() => {
    const parentList = categories.filter(c => !c.parentId);
    const byParent = {};
    categories.forEach(c => {
      if (c.parentId) {
        (byParent[c.parentId] = byParent[c.parentId] || []).push(c);
      }
    });
    // Sort by name
    parentList.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    Object.keys(byParent).forEach(k =>
      byParent[k].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    );
    return { parents: parentList, childrenByParent: byParent };
  }, [categories]);

  // Count of expenses per category (for safety on delete)
  const expenseCountByCategory = useMemo(() => {
    const map = {};
    (state.expenses || []).forEach(e => {
      if (e.categoryId) map[e.categoryId] = (map[e.categoryId] || 0) + 1;
    });
    return map;
  }, [state.expenses]);

  const openAdd = (presetParentId = '') => {
    setEditing(null);
    setFormData({ ...emptyCategory, parentId: presetParentId });
    setShowModal(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setFormData({ ...emptyCategory, ...cat, parentId: cat.parentId || '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return;
    // Guard against making a category its own parent or a cycle via its own descendants
    if (editing && formData.parentId) {
      let walker = lookup[formData.parentId];
      const seen = new Set();
      while (walker && walker.parentId && !seen.has(walker.id)) {
        if (walker.id === editing.id) {
          alert('A category cannot be a descendant of itself.');
          return;
        }
        seen.add(walker.id);
        walker = lookup[walker.parentId];
      }
      if (formData.parentId === editing.id) {
        alert('A category cannot be its own parent.');
        return;
      }
    }
    const payload = {
      name: formData.name.trim(),
      parentId: formData.parentId || null,
      notes: formData.notes || ''
    };
    if (editing) {
      await updateExpenseCategory(editing.id, payload);
    } else {
      await addExpenseCategory(payload);
    }
    setShowModal(false);
  };

  const handleDelete = async (cat) => {
    const childCount = (childrenByParent[cat.id] || []).length;
    const expenseCount = expenseCountByCategory[cat.id] || 0;
    if (childCount > 0) {
      alert(`"${cat.name}" has ${childCount} sub-categor${childCount === 1 ? 'y' : 'ies'}. Reassign or delete those first.`);
      return;
    }
    if (expenseCount > 0) {
      const ok = window.confirm(
        `"${cat.name}" is used by ${expenseCount} expense${expenseCount === 1 ? '' : 's'}. ` +
        `Deleting will leave those expenses uncategorized. Delete anyway?`
      );
      if (!ok) return;
    } else {
      if (!window.confirm(`Delete category "${cat.name}"?`)) return;
    }
    await deleteExpenseCategory(cat.id);
  };

  return (
    <div className="expenses-page">
      <div className="expenses-header">
        <div className="header-title">
          <h1>Expense Categories</h1>
          <div className="subtitle">Organize expenses into categories with optional parent categories for P&amp;L rollup</div>
        </div>
        <div className="header-actions">
          <button className="btn-primary" onClick={() => openAdd('')}>
            <Plus size={16} /> Add Category
          </button>
        </div>
      </div>

      <div className="expenses-table-section">
        {categories.length === 0 ? (
          <div className="expenses-empty">
            <FolderTree size={48} />
            <p>No categories yet — add top-level categories (e.g. "Operating Expenses") then nest sub-categories under them.</p>
          </div>
        ) : (
          <table className="expenses-table">
            <thead>
              <tr>
                <th>Category</th>
                <th>Parent</th>
                <th className="right">Expenses</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {parents.map(parent => {
                const kids = childrenByParent[parent.id] || [];
                return (
                  <React.Fragment key={parent.id}>
                    <tr>
                      <td>
                        <div style={{ fontWeight: 600 }}>{parent.name}</div>
                        {parent.notes && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{parent.notes}</div>}
                      </td>
                      <td>—</td>
                      <td className="right">{expenseCountByCategory[parent.id] || 0}</td>
                      <td>
                        <div className="row-actions">
                          <button onClick={() => openAdd(parent.id)} title="Add sub-category">
                            <Plus size={14} />
                          </button>
                          <button onClick={() => openEdit(parent)} title="Edit">
                            <Pencil size={14} />
                          </button>
                          <button className="delete-btn" onClick={() => handleDelete(parent)} title="Delete">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {kids.map(child => (
                      <tr key={child.id}>
                        <td style={{ paddingLeft: 40 }}>
                          <span style={{ color: 'var(--text-muted)', marginRight: 8 }}>↳</span>
                          {child.name}
                          {child.notes && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', paddingLeft: 20 }}>{child.notes}</div>}
                        </td>
                        <td><span className="account-badge">{parent.name}</span></td>
                        <td className="right">{expenseCountByCategory[child.id] || 0}</td>
                        <td>
                          <div className="row-actions">
                            <button onClick={() => openEdit(child)} title="Edit">
                              <Pencil size={14} />
                            </button>
                            <button className="delete-btn" onClick={() => handleDelete(child)} title="Delete">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
              {/* Orphans: parentId set but parent missing */}
              {categories.filter(c => c.parentId && !lookup[c.parentId]).map(orphan => (
                <tr key={orphan.id}>
                  <td>{orphan.name}</td>
                  <td><span className="status-badge overdue">Missing parent</span></td>
                  <td className="right">{expenseCountByCategory[orphan.id] || 0}</td>
                  <td>
                    <div className="row-actions">
                      <button onClick={() => openEdit(orphan)} title="Edit">
                        <Pencil size={14} />
                      </button>
                      <button className="delete-btn" onClick={() => handleDelete(orphan)} title="Delete">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editing ? 'Edit' : 'Add'} Category</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g. Cleaning Supplies"
                />
              </div>
              <div className="form-group">
                <label>Parent Category (optional)</label>
                <select
                  value={formData.parentId || ''}
                  onChange={e => setFormData({ ...formData, parentId: e.target.value })}
                >
                  <option value="">— None (top-level) —</option>
                  {parents
                    .filter(p => !editing || p.id !== editing.id)
                    .map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  Top-level categories roll up P&amp;L totals for their sub-categories.
                </div>
              </div>
              <div className="form-group">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Optional description"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn-save" onClick={handleSave}>
                {editing ? 'Save Changes' : 'Add Category'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExpenseCategories;
